import { CANVAS, TONES, MONO } from './theme';

/**
 * Per-variant progress dots. On a problem script this is what makes the
 * brute-force → optimized structure legible before you press play: you can see
 * there are two approaches and roughly how long each runs.
 */
export function VariantStrip({
  labels,
  lengths,
  activeVariant,
  stepInVariant,
  onSeekVariant,
}: {
  labels: string[];
  lengths: number[];
  activeVariant: number;
  stepInVariant: number;
  onSeekVariant: (variantIndex: number) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
      {labels.map((label, vi) => {
        const isActive = vi === activeVariant;
        const total = lengths[vi] ?? 0;
        // Collapse long variants to a fixed number of dots so a 30-step
        // approach doesn't overflow the strip on a phone.
        const dots = Math.min(total, 12);
        const filledThrough = isActive
          ? Math.ceil((stepInVariant / Math.max(total, 1)) * dots)
          : vi < activeVariant
            ? dots
            : 0;

        return (
          <button
            key={`${label}-${vi}`}
            type="button"
            onClick={() => onSeekVariant(vi)}
            aria-current={isActive ? 'step' : undefined}
            className="group flex min-w-0 items-center gap-2 rounded-md px-1 py-0.5 focus:outline-none focus-visible:ring-2"
            style={{ color: isActive ? CANVAS.text : CANVAS.textFaint }}
          >
            {/* Guard: a label authored as a full sentence must truncate, not
                collapse the strip into a one-word-per-line column. */}
            <span className="max-w-[16ch] truncate text-[13px] font-semibold sm:max-w-[24ch]" title={label}>
              {label}
            </span>
            <span className="flex items-center gap-[3px]" aria-hidden>
              {Array.from({ length: dots }).map((_, di) => (
                <span
                  key={di}
                  className="size-[5px] rounded-full"
                  style={{
                    backgroundColor:
                      di < filledThrough
                        ? isActive
                          ? TONES.accent
                          : CANVAS.textFaint
                        : CANVAS.panelBorder,
                  }}
                />
              ))}
            </span>
            <span className="sr-only">
              {isActive ? `current approach, step ${stepInVariant} of ${total}` : `jump to ${label}`}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** `Concept  time O(n)  space O(1)` — the badge sitting top-right of the stage. */
export function ComplexityBadge({
  label,
  time,
  space,
}: {
  label: string;
  time?: string;
  space?: string;
}) {
  if (!time && !space) {
    return (
      <span
        className="rounded-xl border px-3 py-1.5 text-[13px] font-bold"
        style={{ borderColor: CANVAS.panelBorder, color: CANVAS.text }}
      >
        {label}
      </span>
    );
  }
  return (
    <span
      className="inline-flex max-w-[240px] flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border px-3 py-1.5 sm:max-w-[320px]"
      style={{ borderColor: CANVAS.panelBorder }}
    >
      <span className="max-w-[22ch] truncate text-[13px] font-bold" style={{ color: CANVAS.text }} title={label}>
        {label}
      </span>
      {time && (
        <span className="text-[12px]" style={{ fontFamily: MONO, color: CANVAS.textDim }}>
          time <span style={{ color: CANVAS.text }}>{time}</span>
        </span>
      )}
      {space && (
        <span className="text-[12px]" style={{ fontFamily: MONO, color: CANVAS.textDim }}>
          space <span style={{ color: CANVAS.text }}>{space}</span>
        </span>
      )}
    </span>
  );
}
